import React from "react";
import { PlxsIcon, PlxsText } from "@plexlogic/plexus-ui";
import { DrawData } from "../../types/DrawData";
import { WizardStepHeader } from "../index";
import WizardFooterNav from "../WizardFooterNav";

interface RadioOption {
  value: string;
  label: React.ReactNode;
  description: React.ReactNode;
  ariaLabel: string;
}

interface RadioButtonGroupProps {
  name: string;
  options: RadioOption[];
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  isDisabled?: boolean;
}

const RadioButtonGroup: React.FC<RadioButtonGroupProps> = ({
  name,
  options,
  value,
  onChange,
  ariaLabel,
  isDisabled = false,
}) => (
  <div
    role="radiogroup"
    aria-label={ariaLabel}
    style={{
      display: "flex",
      flexDirection: "column",
      gap: "var(--plexus-space-md)",
      marginBottom: "var(--plexus-space-sm)",
    }}
  >
    {options.map((opt) => {
      const selected = value === opt.value;
      return (
        <label
          key={opt.value}
          htmlFor={`${name}-${opt.value}`}
          aria-label={opt.ariaLabel}
          tabIndex={isDisabled ? -1 : 0}
          style={{
            display: "flex",
            flexDirection: "column",
            borderRadius: "var(--plexus-space-xs)", // 4px token-based
            border: selected
              ? "var(--plexus-border-width-thin) solid var(--plexus-color-positive-400)"
              : "var(--plexus-border-width-thin) solid var(--plexus-color-neutral-600)",
            background: selected
              ? "var(--plexus-color-positive-50)"
              : "var(--plexus-color-neutral-bg-subtle)",
            padding: "var(--plexus-space-md) var(--plexus-space-lg)", // 16px 24px
            cursor: isDisabled ? "not-allowed" : "pointer",
            minHeight: "72px",
            margin: 0,
            position: "relative",
            fontWeight: selected ? 600 : 500,
            color: "var(--plexus-color-neutral-100)",
            transition: "all 0.2s ease-in-out",
            opacity: isDisabled && !selected ? 0.5 : 1,
          }}
          onMouseEnter={(e) => {
            if (isDisabled) return;
            if (!selected) {
              e.currentTarget.style.borderColor =
                "var(--plexus-color-neutral-600)";
              e.currentTarget.style.backgroundColor =
                "var(--plexus-color-neutral-bg-hover)";
            }
          }}
          onMouseLeave={(e) => {
            if (isDisabled) return;
            if (!selected) {
              e.currentTarget.style.borderColor =
                "var(--plexus-color-neutral-600)";
              e.currentTarget.style.backgroundColor =
                "var(--plexus-color-neutral-bg-subtle)";
            }
          }}
          onKeyDown={(e) => {
            if (isDisabled) return;
            if (e.key === " " || e.key === "Enter") {
              onChange(opt.value);
            }
          }}
        >
          <span
            style={{
              fontSize: "16px",
              color: "var(--plexus-color-body-text)",
              fontWeight: "inherit",
            }}
          >
            {opt.label}
          </span>
          <span
            style={{
              color: "var(--plexus-color-text-description)",
              fontSize: "14px",
              marginTop: "var(--plexus-space-sm)",
              fontWeight: 400,
            }}
          >
            {opt.description}
          </span>
          {selected && (
            <span
              style={{
                position: "absolute",
                top: "var(--plexus-space-md)",
                right: "var(--plexus-space-lg)",
                display: "flex",
                alignItems: "center",
                color: "var(--plexus-color-positive-400)",
              }}
            >
              <PlxsIcon name="checkInCircle" aria-label="Selected" />
            </span>
          )}
          <input
            type="radio"
            id={`${name}-${opt.value}`}
            name={name}
            value={opt.value}
            checked={selected}
            onChange={() => onChange(opt.value)}
            disabled={isDisabled}
            style={{ position: "absolute", left: -9999 }}
            aria-checked={selected}
            aria-label={opt.ariaLabel}
          />
        </label>
      );
    })}
  </div>
);

interface EntryMethodStepProps {
  drawData: DrawData;
  updateDrawData: (updates: Partial<DrawData>) => void;
  onNext: () => void;
  onBack: () => void;
  isLocked?: boolean;
}

const EntryMethodStep: React.FC<EntryMethodStepProps> = ({
  drawData,
  updateDrawData,
  onNext,
  onBack,
  isLocked = false,
}) => {
  const options: RadioOption[] = [
    {
      value: "simple",
      label: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--plexus-space-md)",
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: "17px",
              color: "inherit",
              flex: 1,
              transition: "color 0.2s ease-in-out",
            }}
          >
            I have a total number
          </span>
        </div>
      ),
      description: (
        <PlxsText>
          No file needed – give us your total and we'll pick the winning
          numbers.
        </PlxsText>
      ),
      ariaLabel:
        "I have a total number: No file needed – give us your total and we'll pick the winning numbers.",
    },
    {
      value: "standard",
      label: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--plexus-space-md)",
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: "17px",
              color: "inherit",
              flex: 1,
              transition: "color 0.2s ease-in-out",
            }}
          >
            I have a list – one entry each
          </span>
        </div>
      ),
      description: (
        <PlxsText>
          Upload your entrants, one row per entry – we'll pick the winners. Best
          when each person entered once.
        </PlxsText>
      ),
      ariaLabel:
        "I have a list – one entry each: Upload your entrants, one row per entry – we'll pick the winners. Best when each person entered once.",
    },
    {
      value: "advanced",
      label: (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--plexus-space-md)",
          }}
        >
          <span
            style={{
              fontWeight: 600,
              fontSize: "17px",
              color: "inherit",
              flex: 1,
              transition: "color 0.2s ease-in-out",
            }}
          >
            I have a list – repeat entries or tickets
          </span>
        </div>
      ),
      description: (
        <PlxsText>
          Upload your entrants – we handle repeat entries and tickets, and make
          sure nobody wins twice.
        </PlxsText>
      ),
      ariaLabel:
        "I have a list – repeat entries or tickets: Upload your entrants – we handle repeat entries and tickets, and make sure nobody wins twice.",
    },
  ];

  return (
    <div>
      <WizardStepHeader
        title="How did people enter?"
        description="Choose how you'll give us your entries."
      />

      <RadioButtonGroup
        name="entry-method"
        options={options}
        value={drawData.entryMethod || ""}
        onChange={(value) =>
          updateDrawData({
            entryMethod: value as "simple" | "standard" | "advanced",
          })
        }
        ariaLabel="Entry method options"
        isDisabled={isLocked}
      />

      <WizardFooterNav
        backButton={{
          dataTestId: "back-entry-method",
          label: "Back",
          variant: "secondary",
          tone: "neutral",
          onClick: onBack,
        }}
        continueButton={{
          dataTestId: "next-entry-method",
          label: "Next",
          variant: "primary",
          onClick: onNext,
        }}
      />
    </div>
  );
};

export default EntryMethodStep;
